import { useEffect, useState } from 'react';
import { DollarSign, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/lib/hooks/use-toast';
import { PriceList, MaterialPriceListEntry } from '../types/material.types';
import { materialService } from '../services/material-service';

interface Props {
  materialId: string;
  priceListEntries: MaterialPriceListEntry[];
  onPricesChange: () => void;
}

export function MaterialPrices({ materialId, priceListEntries, onPricesChange }: Props) {
  const { toast } = useToast();
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [originalPrices, setOriginalPrices] = useState<Record<string, string>>({});

  useEffect(() => {
    loadPriceLists();
  }, []);

  useEffect(() => {
    // Build price map from existing entries
    const priceMap: Record<string, string> = {};
    for (const entry of priceListEntries) {
      priceMap[entry.priceListId] = String(entry.price);
    }
    setPrices(priceMap);
    setOriginalPrices(priceMap);
  }, [priceListEntries]);

  const loadPriceLists = async () => {
    try {
      setIsLoading(true);
      const data = await materialService.getPriceLists();
      setPriceLists(data.filter((pl: PriceList) => pl.isActive));
    } catch {
      toast({ title: 'Error al cargar listas de precios', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriceChange = (priceListId: string, value: string) => {
    setPrices((prev) => ({ ...prev, [priceListId]: value }));
  };

  const hasChanges = () => {
    for (const pl of priceLists) {
      const current = prices[pl.id] || '';
      const original = originalPrices[pl.id] || '';
      if (current !== original) return true;
    }
    return false;
  };

  const handleSave = async () => {
    const entries: { materialId: string; price: number }[] = [];

    for (const pl of priceLists) {
      const value = prices[pl.id];
      if (value && parseFloat(value) > 0) {
        entries.push({ materialId, price: parseFloat(value) });
        // We need to save per price list
      }
    }

    // Save to each price list that has changes
    try {
      setIsSaving(true);

      for (const pl of priceLists) {
        const current = prices[pl.id] || '';
        const original = originalPrices[pl.id] || '';
        if (current === original) continue;

        const price = parseFloat(current);
        if (!current || isNaN(price) || price <= 0) {
          // Remove entry if price cleared
          const existingEntry = priceListEntries.find((e) => e.priceListId === pl.id);
          if (existingEntry) {
            await materialService.deletePriceListEntry(pl.id, existingEntry.id);
          }
        } else {
          await materialService.upsertPriceListEntries(pl.id, [{ materialId, price }]);
        }
      }

      toast({ title: 'Precios actualizados', variant: 'success' });
      onPricesChange();
    } catch {
      toast({ title: 'Error al guardar precios', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (priceLists.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">No hay listas de precios configuradas.</p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Precios por Lista
        </h3>
        {hasChanges() && (
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1" />
            )}
            Guardar precios
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {priceLists.map((pl) => {
          const current = prices[pl.id] || '';
          const original = originalPrices[pl.id] || '';
          const isEdited = current !== original;

          return (
            <div key={pl.id} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{pl.name}</span>
                  {pl.isDefault && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Default
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {pl.currency}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={current}
                  onChange={(e) => handlePriceChange(pl.id, e.target.value)}
                  className={`w-32 h-8 text-right ${isEdited ? 'border-primary ring-1 ring-primary/30' : ''}`}
                />
                {isEdited && (
                  <span className="text-[10px] text-primary font-medium w-12">editado</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
